import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Search,
  Users,
  UserPlus,
  UserCheck,
  Clock,
  Filter,
  Sparkles
} from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { matchingService } from '@/services/matchingService'
import { useAuth } from '@/contexts/Auth0Context'
import { useToast } from '@/contexts/ToastContext'
import {
  AvailableMentor,
  Relationship,
  RelationshipStatus
} from '@/types/matching'
import MentorCard from '@/components/matching/MentorCard'
import RequestMentorModal from '@/components/matching/RequestMentorModal'
import RelationshipCard from '@/components/matching/RelationshipCard'
import RateRelationshipModal from '@/components/matching/RateRelationshipModal'

type TabType = 'browse' | 'suggested' | 'active' | 'completed'

const FindMentorsPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { showToast } = useToast()

  const [mentors, setMentors] = useState<AvailableMentor[]>([])
  const [suggestedMentors, setSuggestedMentors] = useState<AvailableMentor[]>([])
  const [relationships, setRelationships] = useState<Relationship[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedExpertise, setSelectedExpertise] = useState<string>('all')
  const [activeTab, setActiveTab] = useState<TabType>('browse')

  // Modal state
  const [selectedMentor, setSelectedMentor] = useState<AvailableMentor | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Rating modal state
  const [ratingRelationship, setRatingRelationship] = useState<Relationship | null>(null)
  const [isRatingSubmitting, setIsRatingSubmitting] = useState(false)

  // Prevent duplicate API calls
  const fetchingMentorsRef = useRef(false)
  const fetchingRelationshipsRef = useRef(false)
  const fetchingSuggestedRef = useRef(false)

  const fetchMentors = useCallback(async () => {
    if (fetchingMentorsRef.current) return

    try {
      fetchingMentorsRef.current = true
      const filters = searchQuery ? { search: searchQuery } : undefined
      const data = await matchingService.getAvailableMentors(filters)
      setMentors(Array.isArray(data) ? data : [])
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch mentors'
      })
    } finally {
      fetchingMentorsRef.current = false
    }
  }, [searchQuery, showToast])

  const fetchRelationships = useCallback(async () => {
    if (fetchingRelationshipsRef.current) return

    try {
      fetchingRelationshipsRef.current = true
      const data = await matchingService.getMyRelationships()
      setRelationships(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to fetch relationships:', error)
    } finally {
      fetchingRelationshipsRef.current = false
    }
  }, [])

  const fetchSuggestedMentors = useCallback(async () => {
    if (fetchingSuggestedRef.current) return

    try {
      fetchingSuggestedRef.current = true
      const data = await matchingService.getSuggestedMentors().catch(() => [])
      setSuggestedMentors(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to fetch suggested mentors:', error)
    } finally {
      fetchingSuggestedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      if (!authLoading && !isAuthenticated) {
        setLoading(false)
      }
      return
    }

    const fetchData = async () => {
      setLoading(true)
      await Promise.all([fetchMentors(), fetchRelationships(), fetchSuggestedMentors()])
      setLoading(false)
    }

    fetchData()
  }, [authLoading, isAuthenticated, fetchMentors, fetchRelationships, fetchSuggestedMentors])

  const handleRequestMentor = (mentor: AvailableMentor) => {
    setSelectedMentor(mentor)
    setIsModalOpen(true)
  }

  const handleSubmitRequest = async (message: string) => {
    if (!selectedMentor) return

    try {
      setIsSubmitting(true)
      await matchingService.requestMentor({
        mentorId: selectedMentor.id,
        message
      })
      showToast({
        type: 'success',
        message: 'Mentorship request sent successfully!'
      })
      setIsModalOpen(false)
      setSelectedMentor(null)
      fetchRelationships()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to send request'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReconnect = (relationship: Relationship) => {
    const mentor = mentors.find((m) => m.id === relationship.mentorId)
    if (!mentor) {
      showToast({
        type: 'error',
        message: 'This mentor is not currently accepting requests.'
      })
      return
    }
    handleRequestMentor(mentor)
  }

  const handleSubmitRating = async (rating: number, review?: string) => {
    if (!ratingRelationship) return
    const wasActive = ratingRelationship.status === RelationshipStatus.ACTIVE

    try {
      setIsRatingSubmitting(true)
      await matchingService.rateRelationship(ratingRelationship.id, { rating, review })

      if (wasActive) {
        try {
          await matchingService.completeRelationship(ratingRelationship.id)
          showToast({ type: 'success', message: 'Mentorship ended and rating submitted!' })
        } catch (completeError) {
          if ((completeError as { code?: string })?.code === 'RATINGS_INCOMPLETE') {
            showToast({
              type: 'success',
              message: "Rating submitted! Waiting on your mentor to finish up."
            })
          } else {
            throw completeError
          }
        }
      } else {
        showToast({ type: 'success', message: 'Rating submitted!' })
      }

      setRatingRelationship(null)
      fetchRelationships()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to submit rating'
      })
    } finally {
      setIsRatingSubmitting(false)
    }
  }

  const handleEndMentorship = (relationship: Relationship) => {
    setRatingRelationship(relationship)
  }

  // Get unique expertise areas from all mentors
  const allExpertise = Array.from(
    new Set(mentors.flatMap((m) => m.expertise))
  ).sort()

  // Filter mentors by selected expertise
  const filteredMentors = mentors.filter((mentor) => {
    if (selectedExpertise === 'all') return true
    return mentor.expertise.includes(selectedExpertise)
  })

  // Check relationship status for each mentor. A mentor/mentee pair can have
  // multiple relationship documents over time (e.g. reconnecting after a
  // completed mentorship), so check across all of them rather than just the
  // first match.
  const getMentorRelationshipStatus = (mentorId: string) => {
    const mentorRelationships = relationships.filter((r) => r.mentorId === mentorId)
    return {
      hasPending: mentorRelationships.some((r) => r.status === RelationshipStatus.PENDING),
      hasActive: mentorRelationships.some((r) => r.status === RelationshipStatus.ACTIVE)
    }
  }

  // Stats
  const stats = {
    availableMentors: mentors.filter(
      (m) => m.isAvailable && m.currentMentees < m.maxMentees
    ).length,
    pendingRequests: relationships.filter(
      (r) => r.status === RelationshipStatus.PENDING
    ).length,
    activeRelationships: relationships.filter(
      (r) => r.status === RelationshipStatus.ACTIVE
    ).length,
    completedRelationships: relationships.filter(
      (r) => r.status === RelationshipStatus.COMPLETED
    ).length
  }

  const activeRelationships = relationships.filter(
    (r) => r.status === RelationshipStatus.ACTIVE
  )

  const completedRelationships = relationships.filter(
    (r) => r.status === RelationshipStatus.COMPLETED
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Find Your Mentor</h1>
          <p className="mt-2 text-gray-600">
            Browse available mentors and send mentorship requests
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Available Mentors</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.availableMentors}
                  </p>
                </div>
                <Users className="w-8 h-8 text-primary-400" />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending Requests</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {stats.pendingRequests}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Mentors</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.activeRelationships}
                  </p>
                </div>
                <UserCheck className="w-8 h-8 text-green-400" />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Mentors</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {mentors.length}
                  </p>
                </div>
                <UserPlus className="w-8 h-8 text-gray-400" />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <button
              onClick={() => setActiveTab('browse')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'browse'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Browse
            </button>
            <button
              onClick={() => setActiveTab('suggested')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'suggested'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Suggested
            </button>
            <button
              onClick={() => setActiveTab('active')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'active'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Active
              {stats.activeRelationships > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                  {stats.activeRelationships}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'completed'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Completed
              {stats.completedRelationships > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full text-xs">
                  {stats.completedRelationships}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Browse Tab */}
        {activeTab === 'browse' && (
          <>
            {/* Search and Filter */}
            <div className="mb-6 space-y-4">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search mentors by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Expertise Filters */}
              {allExpertise.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Filter className="w-5 h-5 text-gray-400" />
                  <button
                    onClick={() => setSelectedExpertise('all')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedExpertise === 'all'
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    All
                  </button>
                  {allExpertise.slice(0, 6).map((expertise) => (
                    <button
                      key={expertise}
                      onClick={() => setSelectedExpertise(expertise)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        selectedExpertise === expertise
                          ? 'bg-primary-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {expertise}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mentors List */}
            {filteredMentors.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <Card className="max-w-md mx-auto">
                  <CardBody>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No Mentors Found
                    </h3>
                    <p className="text-gray-600">
                      {searchQuery || selectedExpertise !== 'all'
                        ? 'Try adjusting your search or filters.'
                        : 'No mentors are currently available. Check back later!'}
                    </p>
                  </CardBody>
                </Card>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredMentors.map((mentor) => {
                  const { hasPending, hasActive } = getMentorRelationshipStatus(mentor.id)
                  return (
                    <MentorCard
                      key={mentor.id}
                      mentor={mentor}
                      onRequestMentor={handleRequestMentor}
                      hasPendingRequest={hasPending}
                      hasActiveRelationship={hasActive}
                      matchScore={mentor.matchScore}
                    />
                  )
                })}
              </div>
            )}
          </>
        )}

        {/* Suggested Tab */}
        {activeTab === 'suggested' && (
          <>
            {suggestedMentors.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <Card className="max-w-md mx-auto">
                  <CardBody>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No Suggested Mentors
                    </h3>
                    <p className="text-gray-600">
                      We don't have any suggested mentors for you right now. Check
                      back once more mentors join the platform.
                    </p>
                  </CardBody>
                </Card>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {suggestedMentors.map((mentor) => {
                  const { hasPending, hasActive } = getMentorRelationshipStatus(mentor.id)
                  return (
                    <MentorCard
                      key={mentor.id}
                      mentor={mentor}
                      onRequestMentor={handleRequestMentor}
                      hasPendingRequest={hasPending}
                      hasActiveRelationship={hasActive}
                      matchScore={mentor.matchScore}
                    />
                  )
                })}
              </div>
            )}
          </>
        )}

        {/* Active Tab */}
        {activeTab === 'active' && (
          <>
            {activeRelationships.length === 0 ? (
              <div className="text-center py-12">
                <UserCheck className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <Card className="max-w-md mx-auto">
                  <CardBody>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No Active Mentorship
                    </h3>
                    <p className="text-gray-600">
                      You don't have any active mentors yet. Send a request from
                      the Browse or Suggested tab to get started!
                    </p>
                  </CardBody>
                </Card>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {activeRelationships.map((relationship) => (
                  <RelationshipCard
                    key={relationship.id}
                    relationship={relationship}
                    userRole="mentee"
                    onEndMentorship={handleEndMentorship}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Completed Tab */}
        {activeTab === 'completed' && (
          <>
            {completedRelationships.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <Card className="max-w-md mx-auto">
                  <CardBody>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No Completed Mentorships
                    </h3>
                    <p className="text-gray-600">
                      Mentorships you've completed will show up here.
                    </p>
                  </CardBody>
                </Card>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {completedRelationships.map((relationship) => (
                  <RelationshipCard
                    key={relationship.id}
                    relationship={relationship}
                    userRole="mentee"
                    onRate={setRatingRelationship}
                    onReconnect={handleReconnect}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Request Modal */}
        {selectedMentor && (
          <RequestMentorModal
            mentor={selectedMentor}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false)
              setSelectedMentor(null)
            }}
            onSubmit={handleSubmitRequest}
            isSubmitting={isSubmitting}
          />
        )}

        {/* Rate Relationship Modal */}
        {ratingRelationship && (
          <RateRelationshipModal
            relationship={ratingRelationship}
            userRole="mentee"
            isOpen={!!ratingRelationship}
            onClose={() => setRatingRelationship(null)}
            onSubmit={handleSubmitRating}
            isSubmitting={isRatingSubmitting}
          />
        )}
      </div>
    </div>
  )
}

export default FindMentorsPage
